import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PatternOverlay from "@/components/PatternOverlay";
import LayerYourSignature from "@/components/LayerYourSignature";

const HERO_IMG =
  "https://images.unsplash.com/photo-1780943004195-3bd30f748872?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwZXJmdW1lJTIwYm90dGxlJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyODc3OTc2fDA&ixlib=rb-4.1.0&q=85";
const EDITORIAL_IMG = "/images/our-house.png";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products", { params: { featured: true } });
        setFeatured(data.slice(0, 4));
      } catch {}
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden grain" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="æxènts hero"
            className="w-full h-full object-cover object-[center_45%] opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/30 to-background" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <div className="text-xs uppercase tracking-[0.32em] text-primary animate-fade-up">
              A modern perfumery · Est. 2026
            </div>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tight mt-8 animate-fade-up" style={{ animationDelay: "120ms" }}>
              Scent as
              <br />
              <span className="block font-script not-italic text-primary text-7xl md:text-9xl lg:text-[11rem] leading-none -translate-x-2 md:-translate-x-4 lg:-translate-x-6">
                Signature
              </span>
            </h1>
            <p className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-muted-foreground animate-fade-up" style={{ animationDelay: "240ms" }}>
              Rare essences, small-batch craftsmanship and cinematic
              compositions—æxènts is a perfumery for the ones who want to be
              remembered.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "360ms" }}>
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="bg-primary text-primary-foreground hover:opacity-90 transition-opacity uppercase tracking-[0.22em] text-sm font-medium px-8 py-4 inline-flex items-center gap-3"
              >
                Explore The Collection <ArrowRight size={16} />
              </Link>
              <Link
                to="/shop?category=unisex"
                className="border border-border text-foreground hover:border-primary hover:text-primary transition-all duration-300 px-8 py-4 uppercase tracking-[0.22em] text-sm"
              >
                Discover Unisex
              </Link>
            </div>
          </div>

          <div className="hidden md:block col-span-4">
            <div className="mt-24 animate-fade-up" style={{ animationDelay: "480ms" }}>
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">
                Featured Note
              </div>
              <div className="font-serif text-3xl leading-tight">
                Oud &amp; Saffron
              </div>
              <div className="mt-4 text-sm text-muted-foreground leading-relaxed">
                A darkened corridor of resin, smoke and warm spice—drawing you
                inward, holding you close.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 md:py-32" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-primary">
                The Collection
              </div>
              <h2 className="font-serif text-5xl md:text-6xl mt-4 tracking-tight">
                Signatures for the seasoned nose.
              </h2>
            </div>
            <Link
              to="/shop"
              className="text-xs uppercase tracking-[0.24em] text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              data-testid="featured-view-all"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* LAYER YOUR SIGNATURE */}
      <LayerYourSignature />

      {/* EDITORIAL / STORY */}
      <section className="pattern-bg py-24 md:py-32 border-t border-border" data-testid="home-story">
        <PatternOverlay opacity={0.09} size={520} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="aspect-[4/5] overflow-hidden bg-secondary">
            <img src={EDITORIAL_IMG} alt="Editorial" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.32em] text-primary">Our House</div>
            <h3 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05]">
              Fragrance as a piece of wearable architecture.
            </h3>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-lg">
              Each composition begins with a mood board—cinema stills, weather,
              cities and memory. From there, our perfumers translate the
              intangible into a bottled experience: layered, unrepeatable,
              unmistakably you.
            </p>
            <ul className="mt-8 grid grid-cols-3 gap-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              <li><div className="text-primary font-serif text-3xl mb-1 normal-case tracking-tight">03</div>Fragrances</li>
              <li><div className="text-primary font-serif text-3xl mb-1 normal-case tracking-tight">07</div>Signatures</li>
              <li><div className="text-primary font-serif text-3xl mb-1 normal-case tracking-tight">40+</div>Ingredients</li>
            </ul>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="pattern-bg py-24 md:py-28 border-t border-border" data-testid="home-newsletter">
        <PatternOverlay opacity={0.12} />
        <div className="max-w-3xl mx-auto text-center px-6">
          <div className="text-xs uppercase tracking-[0.32em] text-primary">Le Journal</div>
          <h4 className="font-serif text-4xl md:text-5xl mt-4">
            Enter the correspondence.
          </h4>
          <p className="mt-4 text-muted-foreground">
            Rare releases, private previews and letters from the perfumer.
          </p>
          <form className="mt-10 flex max-w-md mx-auto gap-3" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="you@yours.com"
              data-testid="home-newsletter-input"
              className="flex-1 bg-transparent border-b border-border px-0 py-3 text-foreground focus:outline-none focus:border-primary text-sm placeholder:text-muted-foreground"
            />
            <button
              type="submit"
              data-testid="home-newsletter-submit"
              className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-6 py-3 hover:opacity-90"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
